import React, { Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useParams,
} from "react-router-dom";
import { Spin } from "antd";
import { AuthProvider } from "./contexts/AuthContext";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import RoleRoute from "./components/RoleRoute";
import ErrorBoundary from "./components/ErrorBoundary";
import GoatCounterTracker from "./components/GoatCounterTracker";
import "antd/dist/reset.css";
import "./App.css";

// Public pages
const Home = React.lazy(() => import("./pages/Home"));
const Blogs = React.lazy(() => import("./pages/Blogs"));
const BlogPostPage = React.lazy(() => import("./pages/BlogPostPage"));
const Forums = React.lazy(() => import("./pages/Forums"));
const ForumTopic = React.lazy(() => import("./pages/ForumTopic"));
const Login = React.lazy(() => import("./pages/Login"));
const Register = React.lazy(() => import("./pages/Register"));
const RegisterComplete = React.lazy(() => import("./pages/RegisterComplete"));
const InternRegistration = React.lazy(
  () => import("./pages/InternRegistration"),
);
const ForgotPassword = React.lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = React.lazy(() => import("./pages/ResetPassword"));
const OpportunitiesPage = React.lazy(() => import("./pages/OpportunitiesPage"));
const OpportunityDetail = React.lazy(() => import("./pages/OpportunityDetail"));
const CoursesPage = React.lazy(() => import("./pages/CoursesPage"));
const ClassesPage = React.lazy(() => import("./pages/ClassesPage"));
const VideosPage = React.lazy(() => import("./pages/VideosPage"));
const CourseDetailPage = React.lazy(() => import("./pages/CourseDetailPage"));
const ContestList = React.lazy(() => import("./pages/ContestList"));
const ContestRegistration = React.lazy(
  () => import("./pages/ContestRegistration"),
);
const ContestRegistrationSuccess = React.lazy(
  () => import("./pages/ContestRegistrationSuccess"),
);
const FAQ = React.lazy(() => import("./pages/FAQ"));
const About = React.lazy(() => import("./pages/About"));
const Contact = React.lazy(() => import("./pages/Contact"));
const PrivacyPolicy = React.lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = React.lazy(() => import("./pages/TermsOfService"));
const CompetitionsHub = React.lazy(() => import("./pages/CompetitionsHub"));
const SubjectPage = React.lazy(() => import("./pages/SubjectPage"));
const SeasonPage = React.lazy(() => import("./pages/SeasonPage"));
const NotFound = React.lazy(() => import("./pages/NotFound"));
const DebugAuth = React.lazy(() => import("./pages/DebugAuth"));
const AdminLogin = React.lazy(() => import("./pages/AdminLogin"));

// Authenticated pages
const FeatureRequest = React.lazy(() => import("./pages/FeatureRequest"));
const Dashboard = React.lazy(() => import("./pages/Dashboard"));
const ChangePassword = React.lazy(() => import("./pages/ChangePassword"));
const ParentProfile = React.lazy(() => import("./pages/ParentProfile"));
const InternProfile = React.lazy(() => import("./pages/InternProfile"));
const ParentCohorts = React.lazy(() => import("./pages/ParentCohorts"));
const ParentOpportunities = React.lazy(
  () => import("./pages/ParentOpportunities"),
);
const Inbox = React.lazy(() => import("./pages/Inbox"));
const StudentOpportunities = React.lazy(
  () => import("./pages/StudentOpportunities"),
);
const TutorMentorOpportunities = React.lazy(
  () => import("./pages/TutorMentorOpportunities"),
);
const InternOpportunities = React.lazy(
  () => import("./pages/InternOpportunities"),
);
const StudentManagement = React.lazy(() => import("./pages/StudentManagement"));
const Subscriptions = React.lazy(() => import("./pages/Subscriptions"));
const TutorPortal = React.lazy(() => import("./pages/TutorPortal"));
const MentorPortal = React.lazy(() => import("./pages/MentorPortal"));
const ManagerPortal = React.lazy(() => import("./pages/ManagerPortal"));
const AdminDashboard = React.lazy(() => import("./pages/AdminDashboard"));
const AdminContestDashboard = React.lazy(
  () => import("./pages/AdminContestDashboard"),
);
const CurriculumListPage = React.lazy(
  () => import("./pages/CurriculumListPage"),
);
const CurriculumDetailPage = React.lazy(
  () => import("./pages/CurriculumDetailPage"),
);
const TopicDetailPage = React.lazy(() => import("./pages/TopicDetailPage"));
const StudentAssignmentsPage = React.lazy(
  () => import("./pages/StudentAssignmentsPage"),
);
const TutorGradingPage = React.lazy(() => import("./pages/TutorGradingPage"));
const StudentLoadPage = React.lazy(() => import("./pages/StudentLoadPage"));
const ParentCurriculumDashboard = React.lazy(
  () => import("./pages/ParentCurriculumDashboard"),
);
const InternDashboard = React.lazy(() => import("./pages/InternDashboard"));

function PageLoader() {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "60vh",
      }}
      data-testid="page-loading-spinner"
    >
      <Spin size="large" />
    </div>
  );
}

const OPPORTUNITY_TYPE_SLUGS = new Set([
  "competition",
  "workshop",
  "internship",
  "scholarship",
  "event",
  "volunteering",
  "course",
  "research",
]);

// Legacy /opportunities/<type> landing pages now redirect to the browse page
// with the matching type filter so old links and indexed URLs keep working.
function OpportunityTypeRedirect() {
  const { type } = useParams();
  if (!type || !OPPORTUNITY_TYPE_SLUGS.has(type)) {
    return <Navigate to="/404" replace />;
  }
  return <Navigate to={`/opportunities?type=${type}`} replace />;
}

function AppRoutes() {
  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <GoatCounterTracker />
      <Navbar />
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/blogs" element={<Blogs />} />
          <Route path="/blogs/:slug" element={<BlogPostPage />} />
          <Route path="/forums" element={<Forums />} />
          <Route path="/forums/:slug" element={<ForumTopic />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route
            path="/feature-request"
            element={
              <RoleRoute>
                <FeatureRequest />
              </RoleRoute>
            }
          />
          <Route path="/login" element={<Login />} />
          <Route path="/portal/a2m-staff" element={<AdminLogin />} />
          <Route path="/register" element={<Register />} />
          <Route path="/register/intern" element={<InternRegistration />} />
          <Route path="/register-complete" element={<RegisterComplete />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route
            path="/reset-password/:uid/:token"
            element={<ResetPassword />}
          />
          <Route
            path="/change-password"
            element={
              <RoleRoute>
                <ChangePassword />
              </RoleRoute>
            }
          />
          <Route path="/opportunities" element={<OpportunitiesPage />} />
          <Route
            path="/opportunities/:type"
            element={<OpportunityTypeRedirect />}
          />
          <Route path="/opportunity/:slug" element={<OpportunityDetail />} />
          <Route path="/registrations" element={<ContestList />} />
          <Route
            path="/registrations/:slug/register"
            element={<ContestRegistration />}
          />
          <Route
            path="/registrations/registration-success"
            element={<ContestRegistrationSuccess />}
          />
          <Route path="/competitions" element={<CompetitionsHub />} />
          <Route path="/courses" element={<CoursesPage />} />
          <Route path="/videos" element={<VideosPage />} />
          <Route path="/courses/:slug" element={<CourseDetailPage />} />
          <Route
            path="/classes"
            element={<Navigate to="/classes/group" replace />}
          />
          <Route path="/classes/group" element={<ClassesPage view="group" />} />
          <Route
            path="/classes/one-on-one"
            element={<ClassesPage view="one-on-one" />}
          />
          <Route
            path="/classes/requests"
            element={<ClassesPage view="requests" />}
          />
          <Route path="/subjects/:category" element={<SubjectPage />} />
          <Route path="/programs/:season" element={<SeasonPage />} />
          <Route path="/debug-auth" element={<DebugAuth />} />
          <Route
            path="/dashboard"
            element={
              <RoleRoute>
                <Dashboard />
              </RoleRoute>
            }
          />
          <Route
            path="/parent-profile"
            element={
              <RoleRoute allowedTypes={["parent"]}>
                <ParentProfile />
              </RoleRoute>
            }
          />
          <Route
            path="/parent-opportunities"
            element={
              <RoleRoute allowedTypes={["parent"]}>
                <ParentOpportunities />
              </RoleRoute>
            }
          />
          <Route
            path="/inbox"
            element={
              <RoleRoute>
                <Inbox />
              </RoleRoute>
            }
          />
          <Route
            path="/student-opportunities"
            element={
              <RoleRoute allowedTypes={["student"]}>
                <StudentOpportunities />
              </RoleRoute>
            }
          />
          <Route
            path="/tutor-mentor-opportunities"
            element={
              <RoleRoute allowedTypes={["tutor", "mentor"]}>
                <TutorMentorOpportunities />
              </RoleRoute>
            }
          />
          <Route
            path="/intern-opportunities"
            element={
              <RoleRoute allowedTypes={["intern"]}>
                <InternOpportunities />
              </RoleRoute>
            }
          />
          <Route
            path="/parent/cohorts"
            element={
              <RoleRoute allowedTypes={["parent"]}>
                <ParentCohorts />
              </RoleRoute>
            }
          />
          <Route
            path="/student-management"
            element={
              <RoleRoute allowedTypes={["parent"]}>
                <StudentManagement />
              </RoleRoute>
            }
          />
          <Route
            path="/subscriptions"
            element={
              <RoleRoute>
                <Subscriptions />
              </RoleRoute>
            }
          />
          <Route
            path="/tutor-portal"
            element={
              <RoleRoute allowedTypes={["tutor", "mentor"]}>
                <TutorPortal />
              </RoleRoute>
            }
          />
          <Route
            path="/mentor-portal"
            element={
              <RoleRoute allowedTypes={["tutor", "mentor"]}>
                <MentorPortal />
              </RoleRoute>
            }
          />
          <Route
            path="/manager-portal"
            element={
              <RoleRoute allowedTypes={["manager"]}>
                <ManagerPortal />
              </RoleRoute>
            }
          />
          <Route
            path="/admin-dashboard"
            element={
              <RoleRoute allowedTypes={["admin", "manager"]}>
                <AdminDashboard />
              </RoleRoute>
            }
          />
          <Route
            path="/admin/contests"
            element={
              <RoleRoute allowedTypes={["admin", "manager"]}>
                <AdminContestDashboard />
              </RoleRoute>
            }
          />
          <Route
            path="/curricula"
            element={
              <RoleRoute>
                <CurriculumListPage />
              </RoleRoute>
            }
          />
          <Route
            path="/curricula/:id"
            element={
              <RoleRoute>
                <CurriculumDetailPage />
              </RoleRoute>
            }
          />
          <Route
            path="/curricula/:id/topics/:topicId"
            element={
              <RoleRoute>
                <TopicDetailPage />
              </RoleRoute>
            }
          />
          <Route
            path="/my-assignments"
            element={
              <RoleRoute>
                <StudentAssignmentsPage />
              </RoleRoute>
            }
          />
          <Route
            path="/grading"
            element={
              <RoleRoute>
                <TutorGradingPage />
              </RoleRoute>
            }
          />
          <Route
            path="/student-load"
            element={
              <RoleRoute>
                <StudentLoadPage />
              </RoleRoute>
            }
          />
          <Route
            path="/parent-curriculum"
            element={
              <RoleRoute>
                <ParentCurriculumDashboard />
              </RoleRoute>
            }
          />
          <Route
            path="/intern-dashboard"
            element={
              <RoleRoute allowedTypes={["intern"]}>
                <InternDashboard />
              </RoleRoute>
            }
          />
          <Route
            path="/intern-profile"
            element={
              <RoleRoute allowedTypes={["intern"]}>
                <InternProfile />
              </RoleRoute>
            }
          />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
      <Footer />
    </Router>
  );
}

function App() {
  return (
    <AuthProvider>
      <ErrorBoundary>
        <AppRoutes />
      </ErrorBoundary>
    </AuthProvider>
  );
}

export default App;
